<template>
  <div class="columns">
    <div class="column is-four-fifths">
      <div v-if="!editing">
        <div v-if="!terminal?.satelliteNetworkConfiguration">
          <p>No network configuration could be found for this Terminal.</p>
        </div>
        <div v-else>
          <b-message type="is-info is-light" :closable="false" class="mb-2">
            Network address information is required for Terminals subscribed to Evolution-based networks (Kymeta Broadband, Lepton).
          </b-message>
          <detail-field-2>
            <template v-slot:label>ETH0 IP Address</template>
            <template v-slot:value>{{ terminal?.satelliteNetworkConfiguration?.eth0IpAddress }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>ETH0 Subnet Mask</template>
            <template v-slot:value>{{ terminal?.satelliteNetworkConfiguration?.eth0SubnetMask }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>SAT0 IP Address</template>
            <template v-slot:value>{{ terminal?.satelliteNetworkConfiguration?.sat0IpAddress }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>SAT0 Subnet Mask</template>
            <template v-slot:value>{{ terminal?.satelliteNetworkConfiguration?.sat0SubnetMask }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>MGMT IP Address</template>
            <template v-slot:value>{{ terminal?.satelliteNetworkConfiguration?.mgmtIpAddress }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>MGMT Subnet Mask</template>
            <template v-slot:value>{{ terminal?.satelliteNetworkConfiguration?.mgmtSubnetMask }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>DHCP Gateway</template>
            <template v-slot:value>{{ terminal?.satelliteNetworkConfiguration?.dhcpGateway }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>DHCP Range Start</template>
            <template v-slot:value>{{ terminal?.satelliteNetworkConfiguration?.dhcpClientIpRangeStart }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>DHCP Range End</template>
            <template v-slot:value>{{ terminal?.satelliteNetworkConfiguration?.dhcpClientIpRangeEnd }}</template>
          </detail-field-2>
        </div>
      </div>
      <div v-else>
        <b-field label="ETH0 IP Address"
        :type="!validIpAddress(networkConfiguration.eth0IpAddress) ? 'is-danger' : ''"
        :message="!validIpAddress(networkConfiguration.eth0IpAddress) ? 'Please enter a valid IP address.' : ''">
          <b-input placeholder="IP Address"
            v-model="networkConfiguration.eth0IpAddress"
            type="text">
          </b-input>
        </b-field>
        <b-field label="ETH0 Subnet Mask"
        :type="!validIpAddress(networkConfiguration.eth0SubnetMask) ? 'is-danger' : ''"
        :message="!validIpAddress(networkConfiguration.eth0SubnetMask) ? 'Please enter a valid IP address.' : ''">
          <b-input placeholder="Subnet Mask"
            v-model="networkConfiguration.eth0SubnetMask"
            type="text">
          </b-input>
        </b-field>
        <b-field label="SAT0 IP Address"
        :type="!validIpAddress(networkConfiguration.sat0IpAddress) ? 'is-danger' : ''"
        :message="!validIpAddress(networkConfiguration.sat0IpAddress) ? 'Please enter a valid IP address.' : ''">
          <b-input placeholder="IP Address"
            v-model="networkConfiguration.sat0IpAddress"
            type="text">
          </b-input>
        </b-field>
        <b-field label="SAT0 Subnet Mask"
        :type="!validIpAddress(networkConfiguration.sat0SubnetMask) ? 'is-danger' : ''"
        :message="!validIpAddress(networkConfiguration.sat0SubnetMask) ? 'Please enter a valid IP address.' : ''">
          <b-input placeholder="IP Address"
            v-model="networkConfiguration.sat0SubnetMask"
            type="text">
          </b-input>
        </b-field>
        <b-field label="MGMT IP Address"
        :type="!validIpAddress(networkConfiguration.mgmtIpAddress) ? 'is-danger' : ''"
        :message="!validIpAddress(networkConfiguration.mgmtIpAddress) ? 'Please enter a valid IP address.' : ''">
          <b-input placeholder="IP Address"
            v-model="networkConfiguration.mgmtIpAddress"
            type="text">
          </b-input>
        </b-field>
        <b-field label="MGMT Subnet Mask"
        :type="!validIpAddress(networkConfiguration.mgmtSubnetMask) ? 'is-danger' : ''"
        :message="!validIpAddress(networkConfiguration.mgmtSubnetMask) ? 'Please enter a valid IP address.' : ''">
          <b-input placeholder="IP Address"
            v-model="networkConfiguration.mgmtSubnetMask"
            type="text">
          </b-input>
        </b-field>          
          <b-button type="is-primary is-outlined" size="is-small" @click="getDHCPCalculatedField">Auto Calculate DHCP values</b-button>
        <b-field label="DHCP Gateway"
        :type="!validIpAddress(networkConfiguration.dhcpGateway) ? 'is-danger' : ''"
        :message="!validIpAddress(networkConfiguration.dhcpGateway) ? 'Please enter a valid IP address.' : ''">
          <b-input placeholder="IP Address"
            v-model="networkConfiguration.dhcpGateway"
            type="text">
          </b-input>
        </b-field>
        <b-field label="DHCP Range: Start"
        :type="!validIpAddress(networkConfiguration.dhcpClientIpRangeStart) ? 'is-danger' : ''"
        :message="!validIpAddress(networkConfiguration.dhcpClientIpRangeStart) ? 'Please enter a valid IP address.' : ''">
          <b-input placeholder="IP Address"
            v-model="networkConfiguration.dhcpClientIpRangeStart" 
            type="text">
          </b-input>
        </b-field>
        <b-field label="DHCP Range: End"
        :type="!validIpAddress(networkConfiguration.dhcpClientIpRangeEnd) ? 'is-danger' : ''"
        :message="!validIpAddress(networkConfiguration.dhcpClientIpRangeEnd) ? 'Please enter a valid IP address.' : ''">
          <b-input placeholder="IP Address"
            v-model="networkConfiguration.dhcpClientIpRangeEnd"
            type="text">
          </b-input>
        </b-field>
      </div>
    </div>
    
    <div class="column is-one-fifth">
      <div class="px-4 action-links-container">
        <p v-if="!editing"><a @click="toggleEditing(true)">Edit Network Config</a></p>
        <div v-else>
          <p><a @click="toggleEditing(false)">Cancel Editing</a></p>
          <p class="mt-2"><a @click="saveConfig">Save Config</a></p>  
        </div>
        
      </div>
    </div>
  </div>
</template>
<script setup>
import { 
  computed, ref, reactive, getCurrentInstance 
} from 'vue';
import { useTerminalsStore } from '../terminals-store';

const store = useTerminalsStore();
const terminal = computed(() => store.state.terminal);
const editing = ref(false);
const networkConfiguration = reactive({ dhcpClientIpRangeStart: null, dhcpClientIpRangeEnd: null, dhcpGateway: null });
const errors = ref([]);
const app = getCurrentInstance();

const toggleEditing = (on) => {
  if (on) {
    Object.assign(networkConfiguration, terminal.value.satelliteNetworkConfiguration || {});
    editing.value = true;
  } else {
    Object.assign(networkConfiguration, { });
    editing.value = false;
  }
}

const validIpAddress = (ipAddress) => {
  const re = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;
  return re.test(ipAddress);
}
const validateDHCPInputs = () => {
  errors.value = [];
  if ((networkConfiguration.eth0IpAddress && networkConfiguration.eth0IpAddress.trim() != '') && (networkConfiguration.eth0SubnetMask && networkConfiguration.eth0SubnetMask.trim() != '')) {
    if (networkConfiguration.eth0IpAddress && !validIpAddress(networkConfiguration.eth0IpAddress)) errors.value.push('Please enter a valid IP Address for "ETH0 IP".');
    if (networkConfiguration.eth0SubnetMask && !validIpAddress(networkConfiguration.eth0SubnetMask)) errors.value.push('Please enter a valid IP Address for "ETH0 Subnet Mask".'); 
  } else {
    errors.value.push('Please enter IP Addresses for "ETH0 IP" and "ETH0 SubnetMask".');
  }
  return errors.value.length == 0;
}
const getDHCPCalculatedField = async () => {
  const isValidated = validateDHCPInputs();
  if (!isValidated) {
    app.proxy.$buefy.toast.open({
      duration: 2000,
      type: 'is-danger',
      message: 'Please enter valid values.'
    });
  }
  
  const payload = {
    eth0IPAddress: networkConfiguration.eth0IpAddress,
    eth0SubnetMask: networkConfiguration.eth0SubnetMask,
    id: terminal.id
  }
  const calculatedFieldsResponse = await store.dispatch('calculateDHCPFields', payload);   
  // catch any errors
  if (!calculatedFieldsResponse.isSuccess) {
    app.proxy.$buefy.toast.open({
      duration: 5000,
      type: 'is-danger',
      message: `Unable to calulate DHCP Fields. Error: ${calculatedFieldsResponse?.data}`
    });
    return null;
  }
  
  networkConfiguration.dhcpClientIpRangeStart = calculatedFieldsResponse.data.dhcpClientIPRangeStart;
  networkConfiguration.dhcpClientIpRangeEnd = calculatedFieldsResponse.data.dhcpClientIPRangeEnd;
  networkConfiguration.dhcpGateway = calculatedFieldsResponse.data.dhcpGateway;
  return true;
}

const validateIpAddresses = () => {
  errors.value = [];
  const netConfig = networkConfiguration;
  if (netConfig.eth0IpAddress && !validIpAddress(netConfig.eth0IpAddress)) errors.value.push('Please enter a valid IP Address for "ETH0 IP".');
  if (netConfig.eth0SubnetMask && !validIpAddress(netConfig.eth0SubnetMask)) errors.value.push('Please enter a valid IP Address for "ETH0 Subnet Mask".');
  if (netConfig.sat0IpAddress && !validIpAddress(netConfig.sat0IpAddress)) errors.value.push('Please enter a valid IP Address for "SAT0 IP".');
  if (netConfig.sat0SubnetMask && !validIpAddress(netConfig.sat0SubnetMask)) errors.value.push('Please enter a valid IP Address for "SAT0 Subnet Mask".');
  if (netConfig.mgmtIpAddress && !validIpAddress(netConfig.mgmtIpAddress)) errors.value.push('Please enter a valid IP Address for "MGMT IP".');
  if (netConfig.mgmtSubnetMask && !validIpAddress(netConfig.mgmtSubnetMask)) errors.value.push('Please enter a valid IP Address for "MGMT Subnet Mask".');
  if (netConfig.dhcpGateway && !validIpAddress(netConfig.dhcpGateway)) errors.value.push('Please enter a valid IP Address for "DHCP Gateway".');
  if (netConfig.dhcpClientIpRangeStart && !validIpAddress(netConfig.dhcpClientIpRangeStart)) errors.value.push('Please enter a valid IP Address for "DCHP Range Start".');
  if (netConfig.dhcpClientIpRangeEnd && !validIpAddress(netConfig.dhcpClientIpRangeEnd)) errors.value.push('Please enter a valid IP Address for "DHCP Range End".');
  
  return errors.value.length == 0;
}

const saveConfig = async () => { 
  // simplify variable reference, for validation
  const componentNetworkConfig = networkConfiguration;

  // all fields blank we will allow to save, regardless of any DHCP calculated values. [ALL FIELDS BLANK]
  if (!componentNetworkConfig?.eth0IpAddress && !componentNetworkConfig?.eth0SubnetMask && !componentNetworkConfig?.sat0IpAddress && !componentNetworkConfig?.sat0SubnetMask && !componentNetworkConfig?.mgmtIpAddress && !componentNetworkConfig?.mgmtSubnetMask && !componentNetworkConfig?.dhcpGateway && !componentNetworkConfig?.dhcpClientIpRangeStart && !componentNetworkConfig?.dhcpClientIpRangeEnd) {
    // if all fields blank, do nothing and continue to save
    // TODO: huh?
  } else {
    // validations on few fields
    // if eth0ip address, or eth0subnetmark not entered, but any other, or even dhcp
    if ((!componentNetworkConfig?.eth0IpAddress || !componentNetworkConfig?.eth0SubnetMask)
        && (!!componentNetworkConfig?.sat0IpAddress || !!componentNetworkConfig?.sat0SubnetMask || !!componentNetworkConfig?.mgmtIpAddress || !!componentNetworkConfig?.mgmtSubnetMask || !!componentNetworkConfig?.dhcpGateway || !!componentNetworkConfig?.dhcpClientIpRangeStart || !!componentNetworkConfig?.dhcpClientIpRangeEnd)) {
      app.proxy.$buefy.toast.open({
        duration: 5000,
        type: 'is-danger',
        message: 'Please enter valid values for ETH0IpAddress or ETH0Subnetmask.'
      });
      return null;
    }
    const componentNetworkConfigDhcpGateway = componentNetworkConfig.dhcpGateway;
    await getDHCPCalculatedField();
    const dhcpGatewayLatest = networkConfiguration?.dhcpGateway;
    // if eth0ip address, eth0subnetmark, and dhcpgateway entered, but dhcp gateway is not matching with latest entered values
    if ((!!componentNetworkConfig?.eth0IpAddress && !!componentNetworkConfig?.eth0SubnetMask) && (componentNetworkConfigDhcpGateway != dhcpGatewayLatest)) {
      app.proxy.$buefy.toast.open({
        duration: 5000,
        type: 'is-danger',
        message: `DHCP Gateway value should be ${dhcpGatewayLatest}.`
      });
      return null;
    }
  }
  // this.isSaving = true;
  const isValidated = validateIpAddresses();
  if (!isValidated) {
    // this.isSaving = false;
    app.proxy.$buefy.toast.open({
      duration: 5000,
      type: 'is-danger',
      message: 'Please enter valid values.'
    });
    return null;
  }

  // assign the RemoteId for proper context
  const saveResult = await store.dispatch('updateNetworkConfig', networkConfiguration);
  // this.isSaving = false;
  // catch any errors
  if (!saveResult.isSuccess) {
    app.proxy.$buefy.toast.open({
      duration: 5000,
      type: 'is-danger',
      message: `Unable to save, Error : ${saveResult?.data}`
    });
    return null;
  }

  editing.value = false;

  // KyAnalytics client plugin, event tracker
  app.proxy.$kya.event('remotes', 'saveTerminalNetworkConfig', 'Save network config clicked.', { terminalId: terminal.value.id });
  return true;
}
</script>
